import { acceptHMRUpdate, defineStore } from 'pinia';
import {
  type AddProductsOptions,
  type CartActionModel,
  type CartType,
  CartTypes,
  type ModifyCartItem,
  modifyCartItemSchema,
  type UpdateCartItem
} from './model';
import {
  type AddProductsToCartMutationVariables,
  type AdyenPaymentDetailsMutation,
  type AdyenSaveStateDataMutation,
  type AvailableShippingMethodFragment,
  type CartFragment,
  type CartItemUpdateInput,
  type CartUserErrorsFragment,
  type CheckoutAgreement,
  type CheckTaxExemptionQuery,
  type PlaceOrderMutation,
  type RemoveTaxExemptionMutation,
  type TaxExemptionUploadResult,
  type UpdateProductsInCartMutationVariables
} from '~/lib/Shop/generated/schema';
import { useCartLink } from './useCartLink';
import { useConfirmationStore } from '~/stores/confirmationStore/confirmationStore';
import { extractError } from '~/utils/extractError';
import { useCartCookie } from '~/composables/useCartCookie';
import type { CartItemPutResponseModel } from '~/server/api/magento/cart/item.put';
import type { UpdateCartResponseModel } from '~/server/api/magento/cart/index.put';
import type { DeleteCartItemResponseModel } from '~/server/api/magento/cart/item.delete';
import type { GetCartResponseModel } from '~/server/api/magento/cart/index.post';
import { usePdpConfiguration } from '~/composables/usePdpConfiguration';
import { useTriggerAddToCartEvent } from '~/stores/cartStore/useTriggerAddToCartEvent';
import { v } from 'h3-valibot';
import { isDefined } from '~/utils/guards/isDefined';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import {
  BASLER_EXPRESS_DELIVERY_CARRIER_CODE,
  BASLER_PICKUP_CARRIER_CODE,
  BASLER_STANDARD_DELIVERY_CARRIER_CODE,
  G4_EVENT_NAME_ADD_SHIPPING_INFO,
  G4_EVENT_NAME_REMOVE_FROM_CART,
  G4_EVENT_NAME_UPDATE_CART
} from '~/lib/constants';

function createFakeFileLike({
  name,
  size,
  type
}: {
  name: string;
  size: number;
  type: string;
}) {
  const realFile = new File([''], name, { type });

  return new Proxy(realFile, {
    get(target, prop) {
      if (prop === 'size') {
        return size;
      }
      return Reflect.get(target, prop);
    }
  });
}

function extractUserErrors(
  userErrors: ReadonlyArray<CartUserErrorsFragment> | undefined | null
) {
  if (!userErrors) {
    return;
  }

  const errors = userErrors.map(
    (error: CartUserErrorsFragment) => error.message
  );

  return errors.length === 0 ? undefined : errors;
}

function retrieveProductSku(cartItem: ModifyCartItem): string {
  if ('sku' in cartItem) {
    return cartItem.sku;
  }

  return cartItem.decoratedProduct!.sku!;
}

/**
 * Provides a store for the shopping cart.
 * GUI Related operations should be moved to a separate store / component /
 * composable and depend on this store.
 */
export const useCartStore = defineStore('shop-cart', () => {
  const {
    $shopStore,
    $locale,
    $globalPageSettings,
    $textDictionary,
    $analytics
  } = useNuxtApp();
  const slug = useSlug();
  const confirmationStore = useConfirmationStore();
  const { loggedIn, user } = useOidcAuth();

  const currentAction = ref<CartActionModel | null>(null);
  const lastAction = ref<CartActionModel | null>(null);
  const loading = ref<boolean>(false);
  const isMerging = ref<boolean>(false);
  const isInitializing = ref<boolean>(false);
  const initialized = ref<boolean>(false);
  const placedOrder = ref<PlaceOrderMutation | null>(null);
  const adyenStateDataId = ref<Record<CartType, string | null>>({
    cart: null,
    requestCart: null,
    customerCart: null
  });
  const currentCouponCode = ref<string | null>(null);
  const pickupDeliveryId = ref<string | null>(null);
  const agreements = ref<CheckoutAgreement[]>([]);

  const carts = ref<Record<CartType, CartFragment | null>>({
    cart: null,
    customerCart: null,
    requestCart: null
  });
  const temporaryAmountChangesPerCart = ref<Record<CartType, number>>({
    cart: 0,
    customerCart: 0,
    requestCart: 0
  });
  const taxExemptionFiles = ref<File[]>([]);
  const uploadedTaxExemptionFiles = ref<File[]>([]);

  const activeCartType = ref<CartType>(
    loggedIn.value ? CartTypes.CUSTOMER_CART : CartTypes.CART
  );

  const relevantCarts = computed(() => {
    if (loggedIn.value) {
      return {
        customerCart: carts.value.customerCart,
        requestCart: carts.value.requestCart
      };
    }
    return {
      cart: carts.value.cart,
      requestCart: carts.value.requestCart
    };
  });

  const relevantCartTypes = computed(() => {
    if (loggedIn.value) {
      return [CartTypes.CUSTOMER_CART, CartTypes.REQUEST_CART];
    }
    return [CartTypes.CART, CartTypes.REQUEST_CART];
  });

  const _state = {
    currentAction,
    lastAction,
    loading,
    isMerging,
    carts,
    relevantCarts,
    activeCartType,
    adyenStateDataId
  };

  const activeCart = computed(() => {
    return carts.value[activeCartType.value];
  });

  const currentCartLink = computed<string | undefined>(() => {
    return useCartLink(activeCartType.value);
  });

  const availableCarts = computed<Record<string, string | undefined | null>>(
    () => {
      return Object.fromEntries(
        Object.entries(cartIds.value).map(([type, storeCartIdMap]) => [
          type,
          storeCartIdMap?.[$shopStore.value]
        ])
      );
    }
  );

  const cartIds = computed(() => {
    return {
      // could use ref directly - but looses actual function to sync cookie
      cart: useCartCookie(CartTypes.CART, $locale.value).value,
      requestCart: useCartCookie(CartTypes.REQUEST_CART, $locale.value).value,
      customerCart: useCartCookie(CartTypes.CUSTOMER_CART, $locale.value).value
    };
  });

  const shopBaseUrlMap = computed<Record<string, string | undefined>>(() => {
    const pdpConfiguration = usePdpConfiguration();

    return {
      default: pdpConfiguration.value?.defaultConfiguration?.baseUrl,
      ...Object.fromEntries(
        Object.entries(pdpConfiguration.value?.configurationByLocale ?? {}).map(
          ([key, value]) => [key, value.baseUrl]
        )
      )
    };
  });

  const profileLink = computed<string>(() => {
    return `${
      shopBaseUrlMap.value[$locale.value] ?? shopBaseUrlMap.value.default
    }/${$shopStore.value}/customer/account/login/`;
  });

  const selectedPaymentMethod = computed(
    () => activeCart.value?.selected_payment_method?.code ?? null
  );

  const hasPayment = computed(() => {
    return activeCart.value?.selected_payment_method?.code !== '';
  });

  const totalExcludingTax = computed(() => {
    return activeCart.value?.prices?.subtotal_excluding_tax;
  });

  const tax = computed(() => {
    if (
      isDefined(activeCart.value?.prices) &&
      isDefined(activeCart.value?.prices?.applied_taxes) &&
      Array.isArray(activeCart.value?.prices?.applied_taxes) &&
      isDefined(activeCart.value?.prices?.applied_taxes?.[0])
    ) {
      return activeCart.value?.prices?.applied_taxes?.[0]?.amount;
    }
    return { value: 0 };
  });

  const hasTax = computed(() => {
    return (
      isDefined(tax.value) && isDefined(tax.value?.value) && tax.value.value > 0
    );
  });

  const total = computed(() => {
    return activeCart.value?.prices?.grand_total;
  });

  const numberOfArticles = computed(() => {
    return (
      (activeCart.value?.total_quantity ?? 0) +
      temporaryAmountChangesPerCart.value[activeCartType.value]
    );
  });

  const overAllNumberOfArticlesInRelevantCarts = computed(() => {
    return (
      Object.values(relevantCarts.value).reduce((acc, cur) => {
        acc += cur?.total_quantity ?? 0;
        return acc;
      }, 0) +
      relevantCartTypes.value.reduce((acc, cartType) => {
        acc += temporaryAmountChangesPerCart.value[cartType];
        return acc;
      }, 0)
    );
  });

  const isCustomerCartActive = computed(() => loggedIn.value);

  const numberOfArticlesCustomerCart = computed(() => {
    return (
      carts.value.customerCart?.total_quantity ??
      0 + temporaryAmountChangesPerCart.value[CartTypes.CUSTOMER_CART]
    );
  });

  const numberOfArticlesCart = computed(() => {
    return (
      carts.value.cart?.total_quantity ??
      0 + temporaryAmountChangesPerCart.value[CartTypes.CART]
    );
  });

  const numberOfArticlesRequestCart = computed(() => {
    return (
      carts.value.requestCart?.total_quantity ??
      0 + temporaryAmountChangesPerCart.value[CartTypes.REQUEST_CART]
    );
  });

  const hasTariff = computed(() => {
    return activeCart.value?.prices?.fooman_applied_surcharges?.length ?? false;
  });

  const tariff = computed(() => {
    return activeCart.value?.prices?.fooman_applied_surcharges?.[0]?.amount;
  });

  const activeCartItems = computed(() => {
    return (
      activeCart.value?.itemsV2?.items.filter(isDefined).map((item) => {
        return {
          ...item,
          decoratedProduct: DecoratedProduct.build(
            item.product,
            $textDictionary.value
          )
        };
      }) ?? []
    );
  });

  const billingIsShippingAddress = computed(() => {
    return shippingAddress.value?.id === billingAddress.value?.id;
  });

  const hasShippingAddress = computed(() => {
    return activeCart.value?.shipping_addresses?.length;
  });

  const shippingAddress = computed(
    () => activeCart.value?.shipping_addresses?.[0] ?? null
  );

  const hasBillingAddress = computed(() => {
    return activeCart.value?.billing_address;
  });

  const billingAddress = computed(() => {
    return activeCart.value?.billing_address;
  });

  const hasItemsOnlyWithStandardShipping = computed(() => {
    // Express should only be available if ALL items have fast delivery
    // Returns true if at least one item does NOT have fast delivery
    return !activeCartItems.value.every(
      (item) => item.decoratedProduct.hasFastDelivery
    );
  });

  const shouldShowPricesInRequestCart = computed(() => {
    // In REQUEST_CART: Hide ALL prices if at least one item has showPrice = false
    if (activeCartType.value !== CartTypes.REQUEST_CART) {
      return true;
    }
    return activeCartItems.value.every(
      (item) => item.decoratedProduct.showPrice
    );
  });

  const availablePaymentMethods = computed(() => {
    return (
      activeCart.value?.available_payment_methods
        ?.filter(isDefined)
        ?.map((pm) => pm.code) ?? []
    );
  });

  const availableShippingMethods = computed(() => {
    const shipMethods = shippingAddress.value?.available_shipping_methods ?? [];

    if (hasItemsOnlyWithStandardShipping.value) {
      return shipMethods
        ?.filter(isDefined)
        ?.filter((i) => i.carrier_code !== BASLER_EXPRESS_DELIVERY_CARRIER_CODE)
        ?.filter(isDefined);
    }

    return shipMethods?.filter(isDefined);
  });

  const isPickupSelected = computed(
    () =>
      selectedShippingMethod.value?.carrier_code === BASLER_PICKUP_CARRIER_CODE
  );

  const selectedShippingMethod = computed(
    () => shippingAddress.value?.selected_shipping_method ?? null
  );

  const appliedCouponCodes = computed(
    () => activeCart.value?.applied_coupons?.filter(isDefined) ?? []
  );

  const appliedDiscounts = computed(
    () => activeCart.value?.prices?.discounts?.filter(isDefined) ?? []
  );

  const handlePickupDeliveryIdUpdate = () => {
    if (
      Array.isArray(activeCart.value?.shipping_addresses) &&
      isDefined(activeCart.value?.shipping_addresses?.[0]) &&
      typeof activeCart.value?.shipping_addresses?.[0] === 'object' &&
      'delivery_id' in (activeCart.value?.shipping_addresses?.[0] ?? {})
    ) {
      const deliveryId = activeCart.value?.shipping_addresses?.[0]?.delivery_id;

      pickupDeliveryId.value = isDefined(deliveryId) ? deliveryId : null;
    }
  };

  /**
   * Adding a product which is already in the cart will increase the
   * amount accordingly.
   * Performs internal error handling.
   *
   * @param cartItems The items to add.
   * @param cartType The cart type to use.
   * @param options Additional options to use.
   */
  async function addProducts(
    cartItems: ReadonlyArray<ModifyCartItem>,
    cartType: CartType,
    options?: AddProductsOptions
  ) {
    const logger = useLogger();
    if (!cartItems.length) {
      logger.warn('cartStore: no elements defined to add to cart - aborting.');

      return;
    }

    const parsedItems = cartItems.map((item) =>
      v.parse(modifyCartItemSchema, item)
    );

    const action: CartActionModel = {
      type: 'addItems'
    };
    currentAction.value = action;

    loading.value = true;

    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      setActiveCartType(cartType);
      const addItemResponse = await $fetch<CartItemPutResponseModel>(
        '/api/magento/cart/item',
        {
          method: 'PUT',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: cartType
          },
          body: {
            cartItems: parsedItems.map((cartItem) => ({
              sku: retrieveProductSku(cartItem),
              quantity: cartItem.quantity,
              config_id: cartItem?.config_id ?? ''
            }))
          } satisfies Pick<AddProductsToCartMutationVariables, 'cartItems'>
        }
      );
      if (addItemResponse) {
        carts.value[cartType] = addItemResponse.cart;
        // XXX: proper typing
        action.error = extractUserErrors(addItemResponse.user_errors as never);
        action.success = true;
      }

      useTriggerAddToCartEvent(
        parsedItems,
        cartType,
        carts.value[cartType]!.id,
        $shopStore.value,
        $locale.value,
        options
      );
    } catch (e) {
      action.error = extractError(e);
      action.success = false;
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
    }
  }

  async function updateProducts(
    updateCartItems: ReadonlyArray<UpdateCartItem>,
    cartType: CartType
  ) {
    const logger = useLogger();
    if (!updateCartItems.length) {
      logger.warn(
        'cartStore: no elements defined to update in cart - aborting.'
      );

      return;
    }
    setActiveCartType(cartType);

    const action: CartActionModel = {
      type: 'updateItems'
    };
    currentAction.value = action;

    loading.value = true;

    const updatedCartItems = updateCartItems
      .filter((updateCartItem) => {
        const cartItem = activeCartItems.value.find(
          (innerItem) => updateCartItem.uid === innerItem.uid
        );

        return !cartItem || updateCartItem.quantity !== cartItem.quantity;
      })
      .map((UpdateCartItem) => {
        const cartItem = activeCartItems.value.find(
          (innerItem) => UpdateCartItem.uid === innerItem.uid
        );

        if (!cartItem) {
          temporaryAmountChangesPerCart.value[activeCartType.value] +=
            UpdateCartItem.quantity;
        } else {
          if (UpdateCartItem.quantity < cartItem.quantity) {
            temporaryAmountChangesPerCart.value[activeCartType.value] +=
              (cartItem.quantity - UpdateCartItem.quantity) * -1;
          } else {
            temporaryAmountChangesPerCart.value[activeCartType.value] +=
              UpdateCartItem.quantity - cartItem.quantity;
          }
        }

        if (cartItem && cartItem.decoratedProduct) {
          return {
            quantity: UpdateCartItem.quantity,
            decoratedProduct: cartItem.decoratedProduct
          };
        }
      })
      .filter(isDefined);

    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      carts.value[activeCartType.value] = await $fetch<UpdateCartResponseModel>(
        '/api/magento/cart',
        {
          method: 'PUT',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value
          },
          body: {
            cartItems: updateCartItems.map(
              (cartItem) =>
                ({
                  cart_item_uid: cartItem.uid,
                  quantity: cartItem.quantity
                } satisfies CartItemUpdateInput)
            )
          } satisfies Pick<UpdateProductsInCartMutationVariables, 'cartItems'>
        }
      );
      handlePickupDeliveryIdUpdate();

      $analytics.pushToDataLayer({
        event: G4_EVENT_NAME_UPDATE_CART,
        userId: user?.value?.claims?.oid?.toString() ?? 'null',
        ecommerce: await createEcommerceTrackingData({
          cartItems: updatedCartItems
        })
      });
    } catch (e) {
      action.error = extractError(e);
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
      temporaryAmountChangesPerCart.value[activeCartType.value] = 0;
    }
  }

  /**
   * Removes the given product from the given cart type.
   * If no cart type is given, the current active cart is used.
   * Performs internal error handling.
   *
   * @param productUid The product uid to remove.
   * @param cartType The cart type to use.
   */

  async function removeFromCart(productUid: string, cartType?: CartType) {
    setActiveCartType(cartType ?? activeCartType.value);

    const action: CartActionModel = {
      type: 'removeItem'
    };
    currentAction.value = action;
    loading.value = true;

    const removedCartItems = [];
    const cartItem = activeCartItems.value.find(
      (innerItem) => productUid === innerItem.uid
    );

    if (cartItem?.decoratedProduct) {
      temporaryAmountChangesPerCart.value[activeCartType.value] =
        cartItem.quantity * -1;
      removedCartItems.push({
        quantity: 0,
        decoratedProduct: cartItem?.decoratedProduct
      });
    }

    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      carts.value[activeCartType.value] =
        await $fetch<DeleteCartItemResponseModel>('/api/magento/cart/item', {
          method: 'DELETE',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            cartItemUid: productUid
          }
        });
      handlePickupDeliveryIdUpdate();

      $analytics.pushToDataLayer({
        event: G4_EVENT_NAME_REMOVE_FROM_CART,
        userId: user?.value?.claims?.oid?.toString() ?? 'null',
        ecommerce: await createEcommerceTrackingData({
          cartItems: removedCartItems
        })
      });
    } catch (e) {
      action.error = extractError(e);
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
      temporaryAmountChangesPerCart.value[activeCartType.value] = 0;
    }
  }

  /**
   * (Re)loads the given cart type.
   * This ensures, that a cart actually exists (if not, a new one
   * is created).
   * If no cart type is given, the current active cart is used.
   * Performs internal error handling.
   *
   * @param type The cart type to load.
   */

  async function loadCart(type?: CartType) {
    if (!loggedIn.value && type === CartTypes.CUSTOMER_CART) {
      return;
    }
    if (loggedIn.value && type === CartTypes.CART) {
      return;
    }

    setActiveCartType(type ?? activeCartType.value);

    const action: CartActionModel = {
      type: 'loadCart'
    };
    currentAction.value = action;

    loading.value = true;

    try {
      carts.value[activeCartType.value] = await $fetch<GetCartResponseModel>(
        '/api/magento/cart',
        {
          method: 'POST',
          query: {
            type: activeCartType.value,
            locale: $locale.value,
            store: $shopStore.value
          }
        }
      );
      handlePickupDeliveryIdUpdate();
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  }

  /**
   * Loads all available carts ({@link this.availableCarts}).
   * Performs a request per available {@link CartType}.
   */
  async function init(once = false) {
    if (isInitializing.value) {
      return;
    }

    isInitializing.value = true;
    if (!once || !initialized.value) {
      await Promise.all(
        Object.entries(availableCarts.value).map(async ([type, cartId]) => {
          if (cartId) {
            await loadCart(type as CartType);
          }
        })
      );
    }

    // display the cart which has actually items.
    // if no cart has "isActive" set - the empty dialog is displayed.
    const firstCartTypeWithItems = Object.entries(carts.value).find(
      ([_, cart]) =>
        cart && cart.itemsV2?.items && cart.itemsV2?.items.length > 0
    )?.[0] as CartType | undefined;
    if (firstCartTypeWithItems) {
      setActiveCartType(firstCartTypeWithItems);
    }

    if (
      [
        $globalPageSettings.value?.shopConfig?.cartPage?.slug,
        $globalPageSettings.value?.shopConfig?.cartCheckoutPage?.slug
      ].includes(slug.value)
    ) {
      setActiveCartType(CartTypes.CART);
    }
    if (
      [
        $globalPageSettings.value?.shopConfig?.requestCartPage?.slug,
        $globalPageSettings.value?.shopConfig?.requestCartCheckoutPage?.slug
      ].includes(slug.value)
    ) {
      setActiveCartType(CartTypes.REQUEST_CART);
    }

    initialized.value = true;
    isInitializing.value = false;
  }

  /**
   * FOR CHECKOUT
   */
  async function initForCheckout() {
    await init(true);
    await checkTaxExemption();
    await loadAgreements();
  }

  async function loadAgreements() {
    const action: CartActionModel = {
      type: 'loadAgreements'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      agreements.value = await $fetch<CheckoutAgreement[]>(
        '/api/magento/checkout/agreements',
        {
          method: 'GET',
          query: {
            store: $shopStore.value,
            locale: $locale.value
          }
        }
      );
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  }

  const addShippingAddress = async function (addressId: number) {
    const action: CartActionModel = {
      type: 'addShippingAddress'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577

      carts.value[activeCartType.value] = await $fetch<CartFragment>(
        '/api/magento/checkout/shipping',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            addressId,
            pickupDeliveryId:
              isDefined(pickupDeliveryId.value) && pickupDeliveryId.value.length
                ? pickupDeliveryId.value
                : undefined
          }
        }
      );
      handlePickupDeliveryIdUpdate();
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const addBillingAddress = async function (addressId: number) {
    const action: CartActionModel = {
      type: 'addBillingAddress'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      carts.value[activeCartType.value] = await $fetch<CartFragment>(
        '/api/magento/checkout/billing',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            addressId
          }
        }
      );
      handlePickupDeliveryIdUpdate();
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const addDeliveryMethod = async function (
    carrierCode: string,
    methodCode: string
  ) {
    const action: CartActionModel = {
      type: 'addDeliveryMethod'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577

      carts.value[activeCartType.value] = await $fetch<CartFragment>(
        '/api/magento/checkout/shippingmethod',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            carrierCode,
            methodCode
          }
        }
      );
      handlePickupDeliveryIdUpdate();

      $analytics.pushToDataLayer({
        event: G4_EVENT_NAME_ADD_SHIPPING_INFO,
        userId: user?.value?.claims?.oid?.toString() ?? 'null',
        ecommerce: {
          ...createEcommerceTrackingData({
            cartItems: activeCartItems.value
              .map((item) => {
                return {
                  quantity: item.quantity,
                  decoratedProduct: item.decoratedProduct
                };
              })
              .filter(isDefined)
          }),
          currency: activeCart.value?.prices?.grand_total?.currency,
          value: activeCart.value?.prices?.grand_total?.value,
          shipping_tier:
            {
              [BASLER_EXPRESS_DELIVERY_CARRIER_CODE]: 'Express',
              [BASLER_PICKUP_CARRIER_CODE]: 'Pickup',
              [BASLER_STANDARD_DELIVERY_CARRIER_CODE]: 'Standard'
            }[carrierCode] ?? 'Standard'
        }
      });
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setPaymentMethod = async function (paymentCode: string) {
    const action: CartActionModel = {
      type: 'setPaymentMethod'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      carts.value[activeCartType.value] = await $fetch<CartFragment>(
        '/api/magento/checkout/payment',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            paymentCode
          }
        }
      );
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setAdyenStateData = async function (stateData: string) {
    const action: CartActionModel = {
      type: 'setAdyenStateData'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      adyenStateDataId.value[activeCartType.value] =
        (
          await $fetch<AdyenSaveStateDataMutation['adyenSaveStateData'] | null>(
            '/api/magento/checkout/payment/stateData',
            {
              method: 'POST',
              query: {
                store: $shopStore.value,
                locale: $locale.value,
                type: activeCartType.value
              },
              body: {
                stateData
              }
            }
          )
        )?.stateDataId ?? null;
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const addAdyenPaymentDetails = async function (stateData: string) {
    const action: CartActionModel = {
      type: 'addAdyenPaymentDetails'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      return (
        (await $fetch<
          AdyenPaymentDetailsMutation['adyenPaymentDetails'] | null
        >('/api/magento/checkout/payment/details', {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value
          },
          body: {
            stateData
          }
        })) ?? null
      );
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const placeOrder = async function () {
    const action: CartActionModel = {
      type: 'placeOrder'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const response = await $fetch<PlaceOrderMutation>(
        '/api/magento/checkout/order',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value
          }
        }
      );

      if (
        isDefined(response.placeOrder) &&
        'errors' in response.placeOrder &&
        Array.isArray(response.placeOrder.errors) &&
        response.placeOrder.errors.length
      ) {
        action.error = response.placeOrder.errors[0]?.message;
      } else {
        placedOrder.value = response;

        confirmationStore.initByOrder(
          placedOrder.value.placeOrder?.order?.order_number ?? '',
          activeCart.value
        );
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const commitPickupDeliveryId = async function () {
    if (
      isDefined(shippingAddress.value?.id) &&
      pickupDeliveryId.value &&
      pickupDeliveryId.value.length > 0
    ) {
      // addShippingAddress handles internal state of pickupLocationCode
      await addShippingAddress(shippingAddress.value.id!);
    }
  };

  const isSelectedShippingMethod = function (
    shippingMethod: AvailableShippingMethodFragment
  ): boolean {
    if (!selectedShippingMethod.value) {
      return false;
    }
    return (
      selectedShippingMethod.value.method_code == shippingMethod.method_code &&
      selectedShippingMethod.value.carrier_code == shippingMethod.carrier_code
    );
  };

  const applyCouponCode = async function (coupon?: string) {
    const couponCode = coupon ?? currentCouponCode.value;
    const action: CartActionModel = {
      type: 'addCoupon'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      carts.value[activeCartType.value] = await $fetch<CartFragment>(
        '/api/magento/checkout/coupon',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            couponCode
          }
        }
      );
      currentCouponCode.value = null;
      handlePickupDeliveryIdUpdate();
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const removeCouponCode = async function (coupon: string) {
    const action: CartActionModel = {
      type: 'removeCoupon'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      carts.value[activeCartType.value] = await $fetch<CartFragment>(
        '/api/magento/checkout/coupon',
        {
          method: 'DELETE',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            type: activeCartType.value,
            couponCode: coupon
          }
        }
      );
      handlePickupDeliveryIdUpdate();
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const checkTaxExemption = async function () {
    const action: CartActionModel = {
      type: 'checkTaxExemption'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const response = await $fetch<
        CheckTaxExemptionQuery['checkTaxExemption']
      >('/api/magento/checkout/taxexemption', {
        method: 'GET',
        query: {
          store: $shopStore.value,
          locale: $locale.value,
          type: activeCartType.value
        }
      });

      if (response && response.success) {
        uploadedTaxExemptionFiles.value = [
          createFakeFileLike({
            name: (() => {
              if (response.name) {
                const strings = response.name.split('/');
                return strings[strings.length - 1];
              }
              return 'Tax Excemption Certificate';
            })(),
            size: response.size ?? 0,
            type: response.type ?? 'unknown'
          })
        ].filter(isDefined);
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const uploadTaxExemption = async function (file: File) {
    const action: CartActionModel = {
      type: 'uploadTaxExemption'
    };
    currentAction.value = action;
    loading.value = true;

    try {
      const formData = new FormData();
      formData.append('files', file);
      const response = await $fetch<TaxExemptionUploadResult>(
        '/api/magento/checkout/taxexemption',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value
          },
          body: formData
        }
      );

      if (response?.success) {
        taxExemptionFiles.value = [];
        uploadedTaxExemptionFiles.value = [file];
        await checkTaxExemption();
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const removeTaxExemption = async function () {
    const action: CartActionModel = {
      type: 'removeTaxExemption'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const response = await $fetch<
        RemoveTaxExemptionMutation['removeTaxExemption']
      >('/api/magento/checkout/taxexemption', {
        method: 'DELETE',
        query: {
          store: $shopStore.value,
          locale: $locale.value,
          type: activeCartType.value
        }
      });

      if (response?.success) {
        uploadedTaxExemptionFiles.value = [];
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setActiveCartType = function (cartType: CartType) {
    if (cartType === CartTypes.CART && loggedIn.value) {
      cartType = CartTypes.CUSTOMER_CART;
    }

    if (cartType === CartTypes.CUSTOMER_CART && !loggedIn.value) {
      cartType = CartTypes.CART;
    }

    activeCartType.value = cartType;
  };

  const mergeGuestIntoCustomerCart = async function () {
    isMerging.value = true;
    const action: CartActionModel = {
      type: 'mergeCarts'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      carts.value.customerCart = await $fetch<CartFragment>(
        '/api/magento/cart/merge',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $locale.value,
            fromType: CartTypes.CART,
            toType: CartTypes.CUSTOMER_CART
          }
        }
      );
      // Clear the old guest cart after successful merge
      carts.value.cart = null;
      // Also clear the cart cookie to prevent reloading an empty cart
      const cartCookie = useCartCookie(CartTypes.CART, $locale.value);
      cartCookie.value = {};
      setActiveCartType(CartTypes.CUSTOMER_CART);
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
      isMerging.value = false;
    }
  };

  /**
   * Checks if a guest cart exists and has products, then merges it into customer cart.
   * This should be called after user logs in or registers.
   * @returns true if merge was performed, false otherwise
   */
  const checkAndMergeGuestCart = async function (): Promise<boolean> {
    // Check if user is logged in
    if (!loggedIn.value) {
      return false;
    }

    // Check if there's a guest cart cookie
    const guestCartCookie = useCartCookie(CartTypes.CART, $locale.value);
    const hasGuestCartCookie = guestCartCookie.value?.[$shopStore.value];

    if (!hasGuestCartCookie) {
      return false;
    }

    try {
      // Explicitly load the guest cart
      // We need to load it directly because loadCart() prevents loading guest cart when logged in
      const guestCart = await $fetch<CartFragment | null>('/api/magento/cart', {
        method: 'POST',
        query: {
          type: CartTypes.CART,
          locale: $locale.value,
          store: $shopStore.value
        }
      });

      // Check if guest cart has products
      const hasProducts =
        guestCart?.itemsV2?.items && guestCart.itemsV2.items.length > 0;

      if (!hasProducts) {
        // No products to merge, just clear the cookie
        guestCartCookie.value = {};
        return false;
      }

      // Store the guest cart temporarily for the merge
      carts.value.cart = guestCart;

      // Perform the merge
      await mergeGuestIntoCustomerCart();

      return true;
    } catch (e) {
      const action: CartActionModel = {
        type: 'mergeCarts',
        error: extractError(e)
      };
      lastAction.value = action;
      return false;
    }
  };

  const onOrderCompleted = function () {
    const activeCartTypeBefore = activeCartType.value;
    carts.value[activeCartTypeBefore] = null;
    activeCartType.value = CartTypes.REQUEST_CART;
    placedOrder.value = null;
    adyenStateDataId.value[activeCartTypeBefore] = null;
    currentCouponCode.value = null;
    pickupDeliveryId.value = null;
    agreements.value = [];
  };

  const onQuoteCompleted = function () {
    carts.value[CartTypes.REQUEST_CART] = null;
    placedOrder.value = null;
    currentCouponCode.value = null;
  };

  const isCart = computed(() =>
    [CartTypes.CART, CartTypes.CUSTOMER_CART].includes(activeCartType.value)
  );

  const isRequestCart = computed(
    () => activeCartType.value === CartTypes.REQUEST_CART
  );

  return {
    _state,
    currentAction,
    lastAction,
    isInitializing,
    loading,
    carts,
    relevantCarts,
    activeCartType,
    activeCart,
    currentCartLink,
    cartIds,
    availableCarts,
    shopBaseUrlMap,
    profileLink,
    addProducts,
    updateProducts,
    removeFromCart,
    init,
    initialized,
    setActiveCartType,
    mergeGuestIntoCustomerCart,
    checkAndMergeGuestCart,

    /**
     * FOR CHECKOUT
     */
    initForCheckout,
    isCart,
    isMerging,
    isRequestCart,
    isCustomerCartActive,
    numberOfArticles,
    numberOfArticlesCart,
    numberOfArticlesRequestCart,
    numberOfArticlesCustomerCart,
    overAllNumberOfArticlesInRelevantCarts,
    agreements,
    totalExcludingTax,
    total,
    hasTax,
    tax,
    hasTariff,
    tariff,
    activeCartItems,
    shouldShowPricesInRequestCart,
    applyCouponCode,
    currentCouponCode,
    removeCouponCode,
    appliedCouponCodes,
    appliedDiscounts,
    availablePaymentMethods,
    billingIsShippingAddress,
    hasShippingAddress,
    shippingAddress,
    hasBillingAddress,
    billingAddress,
    availableShippingMethods,
    selectedShippingMethod,
    isSelectedShippingMethod,
    isPickupSelected,
    pickupDeliveryId,
    commitPickupDeliveryId,
    addShippingAddress,
    addBillingAddress,
    addDeliveryMethod,
    setPaymentMethod,
    setAdyenStateData,
    addAdyenPaymentDetails,
    adyenStateDataId,
    selectedPaymentMethod,
    hasPayment,
    checkTaxExemption,
    removeTaxExemption,
    uploadedTaxExemptionFiles,
    taxExemptionFiles,
    uploadTaxExemption,
    placeOrder,
    placedOrder,

    onOrderCompleted,
    onQuoteCompleted
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
